import { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import UserContext from "common/Context/UserContext";
import CustomAlert from "common/Components/CustomAlert";
import { useResetPasswordRequest } from "./useResetPasswordRequest";
import {
  RESET_PASSWORD_ALERT_TYPE,
  RESET_PASSWORD_FLOW,
} from "./confirmResetPassword.constants";

const { RESET, SUCCESS, FAIL } = RESET_PASSWORD_ALERT_TYPE;

export function useResetPasswordFlow() {
  const navigate = useNavigate();
  const { userProfile } = useContext(UserContext);
  const { state, actions } = useResetPasswordRequest();
  const [showCustomAlert, setShowCustomAlert] = useState(false);
  const [alertInfo, setAlertInfo] = useState({});

  const profile = userProfile?.profile;
  const cognitoUsername =
    profile?.userid != null && String(profile.userid).trim() !== ""
      ? String(profile.userid).trim()
      : "";

  const requestResetPassword = useCallback(() => {
    setAlertInfo({
      title: RESET_PASSWORD_FLOW.alertTitle,
      message: RESET_PASSWORD_FLOW.confirmMessage,
      type: RESET,
    });
    setShowCustomAlert(true);
  }, []);

  useEffect(() => {
    if (!state.loading && state.error) {
      setAlertInfo({
        title: RESET_PASSWORD_FLOW.alertTitle,
        message: state.error,
        type: FAIL,
      });
      setShowCustomAlert(true);
    } else if (!state.loading && state.success) {
      setAlertInfo({
        title: RESET_PASSWORD_FLOW.alertTitle,
        message: RESET_PASSWORD_FLOW.successMessage,
        type: SUCCESS,
      });
      setShowCustomAlert(true);
    }
  }, [state]);

  const handleAlertCallback = useCallback(
    (type) => {
      setAlertInfo({});
      setShowCustomAlert(false);
      switch (type) {
        case RESET:
          actions.resetPassword(cognitoUsername);
          break;
        case SUCCESS:
          actions.reset();
          navigate(RESET_PASSWORD_FLOW.redirectPath, {
            state: {
              username: cognitoUsername,
              email: profile?.email,
            },
          });
          break;
        case FAIL:
          actions.reset();
          break;
        default:
          break;
      }
    },
    [actions, cognitoUsername, navigate, profile?.email]
  );

  useEffect(() => {
    if (!showCustomAlert || alertInfo.type !== SUCCESS) {
      return undefined;
    }

    const timer = setTimeout(() => {
      handleAlertCallback(SUCCESS);
    }, RESET_PASSWORD_FLOW.redirectDelayMs);

    return () => clearTimeout(timer);
  }, [alertInfo.type, handleAlertCallback, showCustomAlert]);

  const resetPasswordAlert = showCustomAlert ? (
    <CustomAlert
      show={showCustomAlert}
      title={alertInfo.title}
      message={alertInfo.message}
      buttonVariant="pill"
      onOkay={
        alertInfo.type === SUCCESS
          ? null
          : () => handleAlertCallback(alertInfo.type)
      }
      onCancel={
        alertInfo.type === RESET ? () => handleAlertCallback(null) : null
      }
    />
  ) : null;

  return { requestResetPassword, resetPasswordAlert };
}
